/**
 * @file useDraftPriorMonth.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-17
 * @modified 2026-10-17
 * @description Pricing & Costing Review D12 (Cam, 17 Oct 2026): approving a
 *              draft invoice dated before this month asks first — "Date it
 *              01/10/2026, as the lock would" or "Keep 25/09/2026, because the
 *              lock is off on purpose". One question for a bulk approval,
 *              naming the invoices. The server refuses with 409
 *              DRAFT_PRIOR_MONTH { details.draft_prior_month } until the
 *              request carries prior_month 'move' | 'keep'
 *              (services/draftPricingService.priorMonthPlan).
 *
 *   const { ask, dialog } = useDraftPriorMonth();
 *   const choice = await ask([plan, …]);   // 'move' | 'keep' | null (cancelled)
 *   … render {dialog}
 */

import React, { useCallback, useRef, useState } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';

const uk = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '');

/** The plan a 409 DRAFT_PRIOR_MONTH answer carries, or null. */
export function priorMonthPlanOf(status, data) {
  if (status !== 409 || data?.code !== 'DRAFT_PRIOR_MONTH') return null;
  return data?.details?.draft_prior_month || null;
}

export default function useDraftPriorMonth() {
  const [state, setState] = useState(null);
  const resolver = useRef(null);

  const ask = useCallback((plans) => new Promise((resolve) => {
    const list = (Array.isArray(plans) ? plans : [plans]).filter(Boolean);
    if (!list.length) { resolve(null); return; }
    resolver.current = resolve;
    setState({ plans: list });
  }), []);

  const finish = (choice) => {
    const r = resolver.current;
    resolver.current = null;
    setState(null);
    if (r) r(choice);
  };

  let dialog = null;
  if (state) {
    const plans = state.plans;
    const keepAllowed = plans.every(p => p.keep_allowed);
    const moveTo = [...new Set(plans.map(p => p.move_to))];
    const one = plans.length === 1 ? plans[0] : null;
    const message = one
      ? one.message
      : `${plans.length} draft invoices are dated before this month (${plans.map(p => `${p.invoice_no} ${uk(p.invoice_date)}`).join(', ')}). `
        + (keepAllowed
          ? `Date them ${moveTo.length === 1 ? uk(moveTo[0]) : 'the first open day of this month'}, as the lock would, or keep their own dates because the lock is off on purpose?`
          : `Xero has locked some of those dates, so they will all be dated ${moveTo.length === 1 ? uk(moveTo[0]) : 'the first open day'}.`);
    const it = one ? 'it' : 'them';
    const buttons = keepAllowed
      ? ` The calendar button dates ${it} this month; the clock button keeps ${one ? 'its own date' : 'their own dates'}.`
      : ` The calendar button approves ${it} with that date.`;
    dialog = (
      <ConfirmDialog
        isOpen
        title={one ? one.title : 'Draft invoices dated last month'}
        message={`${message}${buttons}`}
        type="warning"
        confirmText={moveTo.length === 1 ? `Date ${one ? 'it' : 'them'} ${uk(moveTo[0])}` : 'Date them this month'}
        confirmIcon="event"
        secondaryText={keepAllowed ? `Keep ${one ? uk(one.invoice_date) : 'their dates'}` : null}
        secondaryIcon="history"
        onSecondary={keepAllowed ? () => finish('keep') : null}
        onConfirm={() => finish('move')}
        onCancel={() => finish(null)}
      />
    );
  }
  return { ask, dialog };
}
